import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import WebhookIcon from '@mui/icons-material/Webhook';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DataObjectIcon from '@mui/icons-material/DataObject';
import SettingsIcon from '@mui/icons-material/Settings';

const WebhookEditorDrawer = ({
  open,
  onClose,
  onSave,
  webhook,
  policies,
  formData,
  setFormData,
  isLoading = false,
}) => {
  const [expandedSections, setExpandedSections] = useState(['basic']);
  const isEditing = !!webhook;

  // URL validation
  const isValidUrl = useMemo(() => {
    if (!formData.url) return null;
    try {
      new URL(formData.url);
      return true;
    } catch {
      return false;
    }
  }, [formData.url]);

  // Get selected policy for field mapping suggestions
  const selectedPolicy = useMemo(
    () => policies.find((p) => p.id === formData.policy),
    [formData.policy, policies]
  );

  // Check for duplicate keys in arrays
  const hasDuplicateKeys = (array) => {
    const keys = (array || [])
      .map((item) => (item.key ? item.key.toLowerCase() : ''))
      .filter((key) => key !== '');
    return keys.some((key, index) => keys.indexOf(key) !== index);
  };

  const hasFieldMappingDuplicates = hasDuplicateKeys(formData.fieldMappings);
  const hasHeaderDuplicates = hasDuplicateKeys(formData.headers);
  const canSave = formData.name && formData.url && isValidUrl && !hasFieldMappingDuplicates && !hasHeaderDuplicates;

  // Handle form field changes
  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSelectChange = (field) => (_, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Field mappings handlers
  const handleAddFieldMapping = () => {
    setFormData({
      ...formData,
      fieldMappings: [...(formData.fieldMappings || []), { key: '', value: '' }],
    });
  };

  const handleUpdateFieldMapping = (index, field, value) => {
    const updated = formData.fieldMappings.map((mapping, i) =>
      i === index ? { ...mapping, [field]: value } : mapping
    );
    setFormData({ ...formData, fieldMappings: updated });
  };

  const handleRemoveFieldMapping = (index) => {
    const updated = [...formData.fieldMappings];
    updated.splice(index, 1);
    setFormData({ ...formData, fieldMappings: updated });
  };

  // Headers handlers
  const handleAddHeader = () => {
    setFormData({
      ...formData,
      headers: [...(formData.headers || []), { key: '', value: '' }],
    });
  };

  const handleUpdateHeader = (index, field, value) => {
    const updated = formData.headers.map((header, i) =>
      i === index ? { ...header, [field]: value } : header
    );
    setFormData({ ...formData, headers: updated });
  };

  const handleRemoveHeader = (index) => {
    const updated = [...formData.headers];
    updated.splice(index, 1);
    setFormData({ ...formData, headers: updated });
  };

  // Get available schema keys for field mapping
  const getAvailableSchemaKeys = (currentIndex) => {
    const allKeys = selectedPolicy?.policy?.documentFieldsSchema?.map((f) => f.key) || [];
    allKeys.push('document');
    const selectedKeys = (formData.fieldMappings || [])
      .map((p, i) => (i !== currentIndex ? p.key : null))
      .filter(Boolean);
    return allKeys.filter((key) => !selectedKeys.includes(key));
  };

  const handleAccordionChange = (panel) => (_, expanded) => {
    if (expanded) {
      setExpandedSections((prev) => [...prev, panel]);
    } else {
      setExpandedSections((prev) => prev.filter((p) => p !== panel));
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      size="md"
      slotProps={{
        content: {
          sx: {
            width: { xs: '100vw', sm: 480 },
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
      sx={{
        '--Drawer-horizontalSize': '480px',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 'md',
            bgcolor: 'primary.softBg',
            color: 'primary.600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WebhookIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography level="title-lg">
            {isEditing ? 'Edit Webhook' : 'Create Webhook'}
          </Typography>
          <Typography level="body-sm" color="neutral">
            {isEditing ? 'Modify webhook configuration' : 'Configure a new webhook'}
          </Typography>
        </Box>
        <IconButton variant="plain" color="neutral" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
        <AccordionGroup
          sx={{
            [`& .MuiAccordion-root`]: {
              borderRadius: 'md',
              mb: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              '&:before': { display: 'none' },
            },
          }}
        >
          {/* Basic Info Section */}
          <Accordion
            expanded={expandedSections.includes('basic')}
            onChange={handleAccordionChange('basic')}
          >
            <AccordionSummary>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <SettingsIcon sx={{ color: 'primary.500' }} />
                <Box>
                  <Typography level="title-sm">Basic Information</Typography>
                  <Typography level="body-xs" color="neutral">
                    Name and endpoint URL
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <FormControl required>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={formData.name || ''}
                    onChange={handleChange('name')}
                    placeholder="e.g., Invoice Processor Webhook"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Policy</FormLabel>
                  <Select
                    value={formData.policy || ''}
                    onChange={handleSelectChange('policy')}
                    placeholder="Select policy (optional)"
                  >
                    <Option value="">None</Option>
                    {policies.map((policy) => (
                      <Option key={policy.id} value={policy.id}>
                        {policy.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl required>
                  <FormLabel>Endpoint URL</FormLabel>
                  <Input
                    value={formData.url || ''}
                    onChange={handleChange('url')}
                    placeholder="https://api.example.com/webhook"
                    startDecorator={<LinkIcon sx={{ fontSize: 18 }} />}
                    endDecorator={
                      formData.url && (
                        isValidUrl ? (
                          <CheckCircleOutlineIcon sx={{ color: 'success.500', fontSize: 20 }} />
                        ) : (
                          <ErrorOutlineIcon sx={{ color: 'danger.500', fontSize: 20 }} />
                        )
                      )
                    }
                    color={formData.url ? (isValidUrl ? 'success' : 'danger') : 'neutral'}
                  />
                  <FormHelperText>
                    The URL that will receive POST requests with extraction results
                  </FormHelperText>
                </FormControl>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Field Mappings Section */}
          <Accordion
            expanded={expandedSections.includes('mappings')}
            onChange={handleAccordionChange('mappings')}
          >
            <AccordionSummary>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <DataObjectIcon sx={{ color: 'warning.500' }} />
                <Box>
                  <Typography level="title-sm">Field Mappings</Typography>
                  <Typography level="body-xs" color="neutral">
                    Map extracted fields to custom names
                  </Typography>
                </Box>
                {(formData.fieldMappings?.length || 0) > 0 && (
                  <Chip size="sm" variant="soft" color="warning" sx={{ ml: 'auto' }}>
                    {formData.fieldMappings.length} mapping{formData.fieldMappings.length !== 1 ? 's' : ''}
                  </Chip>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ pt: 1 }}>
                {(formData.fieldMappings || []).map((mapping, idx) => {
                  const isDuplicate = formData.fieldMappings?.some(
                    (m, i) => m.key && mapping.key && i !== idx && m.key.toLowerCase() === mapping.key.toLowerCase()
                  );
                  const availableKeys = getAvailableSchemaKeys(idx);

                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 'sm',
                        bgcolor: 'background.level1',
                        border: isDuplicate ? '1px solid' : 'none',
                        borderColor: 'danger.300',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <FormControl sx={{ width: 140, minWidth: 140, maxWidth: 140 }}>
                          <Input
                            size="sm"
                            placeholder="Output field name"
                            value={mapping.value || ''}
                            onChange={(e) => handleUpdateFieldMapping(idx, 'value', e.target.value)}
                            sx={{
                              '& input': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </FormControl>
                        <Typography level="body-sm" sx={{ py: 0.75, flexShrink: 0 }}>
                          ←
                        </Typography>
                        <FormControl sx={{ width: 140, minWidth: 140, maxWidth: 140 }} error={isDuplicate}>
                          <Select
                            size="sm"
                            value={mapping.key || ''}
                            onChange={(_, val) => handleUpdateFieldMapping(idx, 'key', val)}
                            placeholder="Source field"
                            sx={{
                              '& .MuiSelect-button': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              },
                            }}
                          >
                            {availableKeys.map((key) => (
                              <Option key={key} value={key}>
                                {key}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveFieldMapping(idx)}
                          sx={{ flexShrink: 0 }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      {isDuplicate && (
                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                          Duplicate source field
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                <Button
                  variant="soft"
                  color="neutral"
                  size="sm"
                  startDecorator={<AddRoundedIcon />}
                  onClick={handleAddFieldMapping}
                  disabled={
                    (() => {
                      const allKeys = selectedPolicy?.policy?.documentFieldsSchema?.map((f) => f.key) || [];
                      allKeys.push('document');
                      const selectedKeys = (formData.fieldMappings || []).map((p) => p.key).filter(Boolean);
                      return allKeys.every((key) => selectedKeys.includes(key)) || (formData.fieldMappings || []).some((p) => !p.key);
                    })()
                  }
                >
                  Add Field Mapping
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Custom Headers Section */}
          <Accordion
            expanded={expandedSections.includes('headers')}
            onChange={handleAccordionChange('headers')}
          >
            <AccordionSummary>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <SettingsIcon sx={{ color: 'neutral.500' }} />
                <Box>
                  <Typography level="title-sm">Custom Headers</Typography>
                  <Typography level="body-xs" color="neutral">
                    Add custom HTTP headers to requests
                  </Typography>
                </Box>
                {(formData.headers?.length || 0) > 0 && (
                  <Chip size="sm" variant="soft" color="neutral" sx={{ ml: 'auto' }}>
                    {formData.headers.length} header{formData.headers.length !== 1 ? 's' : ''}
                  </Chip>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ pt: 1 }}>
                {(formData.headers || []).map((header, idx) => {
                  const isDuplicate = formData.headers?.some(
                    (h, i) => h.key && header.key && i !== idx && h.key.toLowerCase() === header.key.toLowerCase()
                  );

                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        borderRadius: 'sm',
                        bgcolor: 'background.level1',
                        border: isDuplicate ? '1px solid' : 'none',
                        borderColor: 'danger.300',
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <FormControl sx={{ width: 120, minWidth: 120, maxWidth: 120 }} error={isDuplicate}>
                          <Input
                            size="sm"
                            placeholder="Header name"
                            value={header.key || ''}
                            onChange={(e) => handleUpdateHeader(idx, 'key', e.target.value)}
                            sx={{
                              '& input': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </FormControl>
                        <FormControl sx={{ flex: 1, minWidth: 0 }}>
                          <Input
                            size="sm"
                            placeholder="Header value"
                            value={header.value || ''}
                            onChange={(e) => handleUpdateHeader(idx, 'value', e.target.value)}
                            sx={{
                              '& input': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </FormControl>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveHeader(idx)}
                          sx={{ flexShrink: 0 }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      {isDuplicate && (
                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                          Duplicate header name
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                <Button
                  variant="soft"
                  color="neutral"
                  size="sm"
                  startDecorator={<AddRoundedIcon />}
                  onClick={handleAddHeader}
                  disabled={(formData.headers || []).some((h) => !h.key)}
                >
                  Add Header
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Authentication Section */}
          <Accordion
            expanded={expandedSections.includes('auth')}
            onChange={handleAccordionChange('auth')}
          >
            <AccordionSummary>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <LockOutlinedIcon sx={{ color: 'success.500' }} />
                <Box>
                  <Typography level="title-sm">Authentication</Typography>
                  <Typography level="body-xs" color="neutral">
                    Optional Basic Auth credentials
                  </Typography>
                </Box>
                {formData.auth_username && (
                  <Chip size="sm" variant="soft" color="success" sx={{ ml: 'auto' }}>
                    Enabled
                  </Chip>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <FormControl>
                  <FormLabel>Username</FormLabel>
                  <Input
                    value={formData.auth_username || ''}
                    onChange={handleChange('auth_username')}
                    placeholder="Enter username"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    value={formData.auth_password || ''}
                    onChange={handleChange('auth_password')}
                    placeholder="Enter password"
                  />
                  <FormHelperText>
                    Credentials will be sent using HTTP Basic Authentication
                  </FormHelperText>
                </FormControl>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1.5,
          bgcolor: 'background.surface',
        }}
      >
        <Button
          variant="plain"
          color="neutral"
          onClick={onClose}
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={onSave}
          disabled={!canSave}
          loading={isLoading}
          sx={{ flex: 2 }}
        >
          {isEditing ? 'Save Changes' : 'Create Webhook'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default WebhookEditorDrawer;
